import { useEffect, useState } from "react";
import { Link, Redirect, useRoute } from "wouter";
import type {
  RefundResponse,
  TransactionResponse,
  VoidResponse,
} from "@orbit/api-zod";
import { getMerchantToken, merchantFetch } from "../lib/merchantAuth";
import { MerchantNav } from "../components/MerchantNav";
import { VoidConfirmModal } from "../components/VoidConfirmModal";
import { RefundForm } from "../components/RefundForm";

/**
 * Merchant Transaction detail — Phase 11.
 *
 * Reads GET /api/v1/tenant/transactions/:id (Phase 10), surfaces the
 * full status/loyalty/refund history, and exposes the void + refund
 * actions where eligible. Eligibility hints are cosmetic; the server
 * is the source of truth and surfaces typed error codes
 * (TRANSACTION_TOO_OLD_TO_VOID, REFUND_EXCEEDS_REMAINING, etc.) which
 * the modal/form propagate to the user.
 */

const VOID_WINDOW_MS = 30 * 60 * 1000;

function statusBadgeClass(status: string): string {
  switch (status) {
    case "COMPLETED":
      return "bg-green-100 text-green-800";
    case "VOIDED":
      return "bg-stone-200 text-stone-700";
    case "PARTIAL_REFUND":
      return "bg-amber-100 text-amber-800";
    case "REFUNDED":
      return "bg-red-100 text-red-800";
    default:
      return "bg-stone-100 text-ink-dark";
  }
}

export function MerchantTransactionDetailPage() {
  const [, params] = useRoute("/merchant/transactions/:id");
  const id = params?.id;
  if (!getMerchantToken()) return <Redirect to="/merchant/login" />;
  if (!id) return <Redirect to="/merchant/transactions" />;

  const [tx, setTx] = useState<TransactionResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [refundBusy, setRefundBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  async function reload() {
    const r = await merchantFetch<TransactionResponse>(
      `/api/v1/tenant/transactions/${id}`,
    );
    if (!r.ok) {
      setErr(`${r.code}: ${r.message}`);
      return;
    }
    setTx(r.data);
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function onVoid(reason: string) {
    const r = await merchantFetch<VoidResponse>(
      `/api/v1/tenant/transactions/${id}/void`,
      { method: "POST", body: JSON.stringify({ reason }) },
    );
    if (!r.ok) {
      throw new Error(`${r.code}: ${r.message}`);
    }
    setActionMsg("Transaction voided.");
    setShowVoidModal(false);
    await reload();
  }

  async function onRefund(amount: string, reason: string) {
    setRefundBusy(true);
    try {
      const r = await merchantFetch<RefundResponse>(
        `/api/v1/tenant/transactions/${id}/refund`,
        { method: "POST", body: JSON.stringify({ amount, reason }) },
      );
      if (!r.ok) {
        throw new Error(`${r.code}: ${r.message}`);
      }
      setActionMsg(
        r.data.transaction.status === "REFUNDED"
          ? "Fully refunded."
          : "Partial refund applied.",
      );
      await reload();
    } finally {
      setRefundBusy(false);
    }
  }

  if (err) {
    return (
      <div className="min-h-screen pb-24">
        <div className="mx-auto max-w-2xl px-6 pt-8">
          <MerchantNav current="transactions" />
          <div className="mt-6 rounded-card border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {err}
          </div>
          <Link
            href="/merchant/transactions"
            className="mt-4 inline-block text-navy underline text-sm"
          >
            ← Back to transactions
          </Link>
        </div>
      </div>
    );
  }

  if (!tx) {
    return (
      <div className="min-h-screen pb-24">
        <div className="mx-auto max-w-2xl px-6 pt-8">
          <MerchantNav current="transactions" />
          <p className="mt-6 text-sm text-ink-dark">Loading…</p>
        </div>
      </div>
    );
  }

  const ageMs = Date.now() - new Date(tx.createdAt).getTime();
  const withinVoidWindow = ageMs <= VOID_WINDOW_MS;
  const canVoid =
    tx.status === "COMPLETED" && withinVoidWindow;
  const canRefund =
    (tx.status === "COMPLETED" || tx.status === "PARTIAL_REFUND") &&
    tx.amount !== null;

  return (
    <div className="min-h-screen pb-24">
      <div className="mx-auto max-w-2xl px-6 pt-8">
        <MerchantNav current="transactions" />

        <Link
          href="/merchant/transactions"
          className="mt-4 inline-block text-sm text-navy underline"
        >
          ← Back
        </Link>

        <header className="mt-3 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-navy">
              {tx.amount ? `₱${tx.amount}` : "Visit-only"}
            </h1>
            <p className="text-xs text-ink-dark">
              {new Date(tx.createdAt).toLocaleString()}
            </p>
          </div>
          <span
            className={`rounded-pill px-3 py-1 text-xs font-semibold uppercase ${statusBadgeClass(
              tx.status,
            )}`}
          >
            {tx.status.replace("_", " ")}
          </span>
        </header>

        {actionMsg && (
          <div className="mt-4 rounded-card border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            {actionMsg}
          </div>
        )}

        <section className="mt-5 card">
          <h2 className="text-sm font-semibold text-navy">Details</h2>
          <dl className="mt-2 grid grid-cols-2 gap-y-2 text-xs text-ink-dark">
            <dt>Transaction</dt>
            <dd className="font-mono break-all">{tx.id}</dd>
            <dt>Customer</dt>
            <dd className="font-mono break-all">{tx.customerId}</dd>
            {tx.refundedAmount !== null && (
              <>
                <dt>Refunded so far</dt>
                <dd className="font-mono">₱{tx.refundedAmount}</dd>
              </>
            )}
            {tx.refundedAt && (
              <>
                <dt>Last refund at</dt>
                <dd>{new Date(tx.refundedAt).toLocaleString()}</dd>
              </>
            )}
            {tx.refundReason && (
              <>
                <dt>Refund reason</dt>
                <dd>{tx.refundReason}</dd>
              </>
            )}
            {tx.voidedAt && (
              <>
                <dt>Voided at</dt>
                <dd>{new Date(tx.voidedAt).toLocaleString()}</dd>
              </>
            )}
            {tx.voidReason && (
              <>
                <dt>Void reason</dt>
                <dd>{tx.voidReason}</dd>
              </>
            )}
            <dt>Loyalty reversed</dt>
            <dd>
              {tx.pointsReversed.toFixed(2)} pts · {tx.stampsReversed} stamps
            </dd>
            {tx.loyaltyCreditSnapshot && (
              <>
                <dt>Original credit</dt>
                <dd>
                  {tx.loyaltyCreditSnapshot.pointsCredited.toFixed(2)} pts ·{" "}
                  {tx.loyaltyCreditSnapshot.stampsCredited} stamps
                </dd>
              </>
            )}
          </dl>
        </section>

        {canVoid && (
          <section className="mt-5 card">
            <h2 className="text-sm font-semibold text-navy">Void</h2>
            <p className="mt-1 text-xs text-ink-dark">
              Available within 30 minutes of the scan. Reverses loyalty +
              financial in one step.
            </p>
            <button
              type="button"
              onClick={() => setShowVoidModal(true)}
              className="btn-secondary mt-3 w-full"
            >
              Void transaction
            </button>
          </section>
        )}

        {!canVoid && tx.status === "COMPLETED" && (
          <section className="mt-5 card text-xs text-ink-dark">
            Void window has elapsed (30 minutes). Use refund instead.
          </section>
        )}

        {canRefund && (
          <section className="mt-5 card">
            <h2 className="text-sm font-semibold text-navy">Refund</h2>
            <p className="mt-1 text-xs text-ink-dark">
              Partial or full. Loyalty reverses pro-rata when the cumulative
              refund equals the original amount.
            </p>
            <div className="mt-3">
              <RefundForm
                amount={tx.amount}
                refundedSoFar={tx.refundedAmount}
                onSubmit={onRefund}
                busy={refundBusy}
              />
            </div>
          </section>
        )}

        <VoidConfirmModal
          open={showVoidModal}
          onClose={() => setShowVoidModal(false)}
          onConfirm={onVoid}
          customerName={""}
          amount={tx.amount}
        />
      </div>
    </div>
  );
}
